import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import UiContent from "../../../Components/Common/UiContent";
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import { 
    Card, 
    CardBody, 
    CardHeader,
    Col, 
    Container, 
    Row,
    Table,
    Badge,
    Button,
    Alert,
    Spinner,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input
} from 'reactstrap';
import CountUp from "react-countup";
import axios from 'axios';
import { formatDateTime, formatDuration, getTimezoneInfo } from '../../../utils/dateTimeUtils';

const JobManagementDashboard = () => {
    document.title = "Job Management | AI Financial Prediction Tracker";

    const renderCountRef = useRef(0);
    renderCountRef.current += 1;
    console.log(`🔄 Component render #${renderCountRef.current}`);

    const { user } = useSelector((state) => state.Login);
    const [loading, setLoading] = useState(true);
    const [jobStatus, setJobStatus] = useState(null);
    const [jobExecutions, setJobExecutions] = useState([]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [simpleExecutions, setSimpleExecutions] = useState([]);
    
    // API Base URL - Define early so it can be used in functions
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    
    // Simple test function
    const loadSimpleData = async () => {
        try {
            console.log('🔄 Loading simple data...');
            const response = await axios.get(`${API_BASE_URL}/api/jobs/executions?limit=5`);
            console.log('✅ Simple data response:', response.data);
            
            if (response.data.success && response.data.data) {
                console.log('✅ Simple data received, length:', response.data.data.length);
                setSimpleExecutions(response.data.data);
                console.log('✅ Simple executions set to:', response.data.data);
                
                // Check if data persists after a delay
                setTimeout(() => {
                    console.log('🕐 Checking simple executions after 2 seconds...');
                    console.log('Current simpleExecutions:', simpleExecutions);
                }, 2000);
            }
        } catch (error) {
            console.error('❌ Simple data error:', error);
        }
    };

    const [triggerModal, setTriggerModal] = useState(false);
    const [selectedJob, setSelectedJob] = useState('');
    const [selectedSymbol, setSelectedSymbol] = useState('BTC');
    const [includeTechnicalIndicators, setIncludeTechnicalIndicators] = useState(true);
    const [selectedModel, setSelectedModel] = useState('');
    const [selectedTimeframe, setSelectedTimeframe] = useState('');
    const [triggerLoading, setTriggerLoading] = useState(false);
    // Reprocess log file states
    const [reprocessFilename, setReprocessFilename] = useState('');
    const [reprocessLoading, setReprocessLoading] = useState(false);
    const [reprocessResult, setReprocessResult] = useState(null);
    // Resend AI query from query-log states
    const [resendFilename, setResendFilename] = useState('');
    const [resendPriceOverride, setResendPriceOverride] = useState('');
    const [resendLoading, setResendLoading] = useState(false);
    const [resendResult, setResendResult] = useState(null);
    // FMP 4h collection manual trigger state
    const [fmpCollectLoading, setFmpCollectLoading] = useState(false);
    // FMP 1h collection manual trigger state
    const [fmpCollect1hLoading, setFmpCollect1hLoading] = useState(false);
    // Add loaders for 1d and 1w
    const [fmpCollect1dLoading, setFmpCollect1dLoading] = useState(false);
    const [fmpCollect1wLoading, setFmpCollect1wLoading] = useState(false);
    // ETF data collection (Alpha Vantage + Yahoo) manual trigger state
    const [etfCollectLoading, setEtfCollectLoading] = useState(false);
    // Manual scoring (rescore) state
    const [rescoreSince, setRescoreSince] = useState(''); // datetime-local string
    const [rescoreLoading, setRescoreLoading] = useState(false);
    // Process performance state
    const [processPerformanceLoading, setProcessPerformanceLoading] = useState(false);
    // Process score state
    const [processScoreLoading, setProcessScoreLoading] = useState(false);
    
    // Screenshot & Video Automation states
    const [screenshotUrl, setScreenshotUrl] = useState('');
    const [screenshotAsset, setScreenshotAsset] = useState('');
    const [screenshotLoading, setScreenshotLoading] = useState(false);
    const [btcImages, setBtcImages] = useState('');
    const [btcVideoLoading, setBtcVideoLoading] = useState(false);
    const [briefingImages, setBriefingImages] = useState('');
    const [briefingVideoLoading, setBriefingVideoLoading] = useState(false);
    const [videoFilePath, setVideoFilePath] = useState('');
    const [videoPrivacy, setVideoPrivacy] = useState('public');
    const [publishLoading, setPublishLoading] = useState(false);
    const [automationDateLabel, setAutomationDateLabel] = useState('');
    const [automationLoading, setAutomationLoading] = useState(false);
    const [screenshotsDateLabel, setScreenshotsDateLabel] = useState('');
    const [screenshotsLoading, setScreenshotsLoading] = useState(false);
    const [timeframeLoading4h, setTimeframeLoading4h] = useState(false);
    const [timeframeLoading7d, setTimeframeLoading7d] = useState(false);
    const [timeframeLoading30d, setTimeframeLoading30d] = useState(false);
    const [timeframeLoading180d, setTimeframeLoading180d] = useState(false);
    const [videoResult, setVideoResult] = useState(null);

    // Simple admin check - replace with actual user role check
    const isAdmin = user?.email === 'houzhip@gmail.com' || user?.role === 'admin' || user?.username === 'admin';

    // Debug: Test API call directly
    const testAPICall = async () => {
        try {
            console.log('🧪 Testing API call directly...');
            const response = await axios.get(`${API_BASE_URL}/api/jobs/executions?limit=5`);
            console.log('🧪 Direct API response:', response.data);
            alert(`API working! Got ${response.data.data?.length || 0} records`);
        } catch (error) {
            console.error('🧪 Direct API error:', error);
            alert(`API Error: ${error.message}`);
        }
    };

    // Load job data
    useEffect(() => {
        if (isAdmin) {
            loadJobData();
        }
    }, [isAdmin]);

    // Debug: Log jobExecutions when it changes
    useEffect(() => {
        console.log('🔄 jobExecutions state changed:', jobExecutions);
        console.log('🔄 jobExecutions length:', jobExecutions?.length);
        console.log('🔄 jobExecutions is array:', Array.isArray(jobExecutions));
        console.log('🔄 Current time:', new Date().toLocaleTimeString());
    }, [jobExecutions]);

    const loadJobData = async () => {
        console.log('🔄 loadJobData called');
        try {
            setLoading(true);
            setError(''); // Clear previous errors
            
            console.log('🌐 Making API calls to:', API_BASE_URL);
            
            // First, let's just get the executions to see if that works
            const executionsResponse = await axios.get(`${API_BASE_URL}/api/jobs/executions?limit=20`);
            console.log('✅ Executions API call completed');
            console.log('Executions Response:', executionsResponse.data);
            
            if (executionsResponse.data?.success && executionsResponse.data?.data) {
                console.log('✅ Executions response successful');
                console.log('Raw executions data:', executionsResponse.data.data);
                console.log('Executions data type:', typeof executionsResponse.data.data);
                console.log('Executions data is array:', Array.isArray(executionsResponse.data.data));
                console.log('Executions data length:', executionsResponse.data.data.length);
                
                // Force update the state
                setJobExecutions([...executionsResponse.data.data]);
                
                console.log('✅ setJobExecutions called with array copy');
            } else {
                console.log('❌ Executions response not successful:', executionsResponse.data);
                setError(`Failed to load job executions: ${executionsResponse.data?.error || 'Unknown error'}`);
            }
            
            // Now get the status
            const statusResponse = await axios.get(`${API_BASE_URL}/api/jobs/status`);
            console.log('✅ Status API call completed');
            console.log('Status Response:', statusResponse.data);
            
            if (statusResponse.data?.success) {
                console.log('✅ Setting job status:', statusResponse.data.data);
                setJobStatus(statusResponse.data.data);
            } else {
                console.log('❌ Status response not successful:', statusResponse.data);
            }
            
        } catch (error) {
            console.error('Error loading job data:', error);
            if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
                setError(`Cannot connect to backend server at ${API_BASE_URL}. Please ensure the backend server is running.`);
            } else {
                setError(`Failed to load job data: ${error.message}`);
            }
        } finally {
            setLoading(false);
        }
    };

    // Helper function to check if job requires symbol selection
    const isPredictionJob = (jobName) => {
        return ['ShortTermPredictions', 'LongTermPredictions', 'SpecificPrediction'].includes(jobName);
    };

    // Helper function to close modal and reset selections
    const closeTriggerModal = () => {
        setTriggerModal(false);
        setSelectedJob('');
        setSelectedSymbol('BTC');
        setIncludeTechnicalIndicators(true);
        setSelectedModel('');
        setSelectedTimeframe('');
    };

    const triggerJob = async (jobName) => {
        try {
            setTriggerLoading(true);
            
            const requestBody = {
                jobName: jobName,
                triggerUser: user?.username || user?.email || 'admin'
            };
            
            // Add symbol and technical analysis setting for prediction jobs
            if (['ShortTermPredictions', 'LongTermPredictions'].includes(jobName)) {
                requestBody.symbol = selectedSymbol;
                requestBody.includeTechnicalIndicators = includeTechnicalIndicators;
            } else if (jobName === 'SpecificPrediction') {
                requestBody.symbol = selectedSymbol;
                requestBody.includeTechnicalIndicators = includeTechnicalIndicators;
                requestBody.model = selectedModel;
                requestBody.timeframe = selectedTimeframe;
            }
            
            const response = await axios.post(`${API_BASE_URL}/api/jobs/trigger`, requestBody);

            if (response.data.success) {
                const message = response.data.data?.message || `Job ${jobName} triggered successfully`;
                setSuccess(message);
                closeTriggerModal();
                // Temporarily disable auto-refresh to debug
                // setTimeout(() => {
                //     loadJobData(); // Refresh data after a delay
                // }, 2000);
            } else {
                setError(response.data.error || 'Failed to trigger job');
            }
        } catch (error) {
            console.error('Error triggering job:', error);
            setError('Failed to trigger job');
        } finally {
            setTriggerLoading(false);
        }
    };

    const controlScheduler = async (action) => {
        try {
            const response = await axios.post(`${API_BASE_URL}/api/jobs/control`, {
                action: action
            });

            if (response.data.success) {
                setSuccess(`Job scheduler ${action} completed`);
                loadJobData();
            } else {
                setError(response.data.error || `Failed to ${action} scheduler`);
            }
        } catch (error) {
            console.error('Error controlling scheduler:', error);
            setError(`Failed to ${action} scheduler`);
        }
    };

    // Trigger FMP 4h data collection for all supported symbols (uses backend scheduler queue)
    const triggerFMPCollect4hAll = async () => {
        try {
            setFmpCollectLoading(true);
            setError('');
            setSuccess('');
            const response = await axios.post(`${API_BASE_URL}/api/jobs/fmp/collect-4h`, {});
            if (response.data?.success) {
                const { enqueued = [], concurrency, queueSize, active } = response.data?.data || {};
                const count = Array.isArray(enqueued) ? enqueued.length : 0;
                setSuccess(`Enqueued ${count} 4h FMP collection job${count === 1 ? '' : 's'} (concurrency=${concurrency}, queue=${queueSize}, active=${active}).`);
            } else {
                setError(response.data?.error || 'Failed to trigger FMP 4h collection');
            }
        } catch (err) {
            console.error('Error triggering FMP 4h collection:', err);
            if (err?.response?.status === 403) {
                setError('FMP scheduler is disabled in this environment or backend is not in production.');
            } else {
                setError(err?.response?.data?.error || err.message || 'Failed to trigger FMP 4h collection');
            }
        } finally {
            setFmpCollectLoading(false);
        }
    };

    const triggerFMPCollect1hAll = async () => {
        try {
            setFmpCollect1hLoading(true);
            setError('');
            setSuccess('');
            const response = await axios.post(`${API_BASE_URL}/api/jobs/fmp/collect-1h`, {});
            if (response.data?.success) {
                const { enqueued = [], concurrency, queueSize, active } = response.data?.data || {};
                const count = Array.isArray(enqueued) ? enqueued.length : 0;
                setSuccess(`Enqueued ${count} 1h FMP collection job${count === 1 ? '' : 's'} (concurrency=${concurrency}, queue=${queueSize}, active=${active}).`);
            } else {
                setError(response.data?.error || 'Failed to trigger FMP 1h collection');
            }
        } catch (err) {
            console.error('Error triggering FMP 1h collection:', err);
            setError(err?.response?.data?.error || err.message || 'Failed to trigger FMP 1h collection');
        } finally {
            setFmpCollect1hLoading(false);
        }
    };

    const triggerFMPCollect1dAll = async () => {
        try {
            setFmpCollect1dLoading(true);
            setError('');
            setSuccess('');
            const response = await axios.post(`${API_BASE_URL}/api/jobs/fmp/collect-1d`, {});
            if (response.data?.success) {
                const { enqueued = [], concurrency, queueSize, active } = response.data?.data || {};
                const count = Array.isArray(enqueued) ? enqueued.length : 0;
                setSuccess(`Enqueued ${count} 1d FMP collection job${count === 1 ? '' : 's'} (concurrency=${concurrency}, queue=${queueSize}, active=${active}).`);
            } else {
                setError(response.data?.error || 'Failed to trigger FMP 1d collection');
            }
        } catch (err) {
            console.error('Error triggering FMP 1d collection:', err);
            setError(err?.response?.data?.error || err.message || 'Failed to trigger FMP 1d collection');
        } finally {
            setFmpCollect1dLoading(false);
        }
    };

    const triggerFMPCollect1wAll = async () => {
        try {
            setFmpCollect1wLoading(true);
            setError('');
            setSuccess('');
            const response = await axios.post(`${API_BASE_URL}/api/jobs/fmp/collect-1w`, {});
            if (response.data?.success) {
                const { enqueued = [], concurrency, queueSize, active } = response.data?.data || {};
                const count = Array.isArray(enqueued) ? enqueued.length : 0;
                setSuccess(`Enqueued ${count} 1w FMP collection job${count === 1 ? '' : 's'} (concurrency=${concurrency}, queue=${queueSize}, active=${active}).`);
            } else {
                setError(response.data?.error || 'Failed to trigger FMP 1w collection');
            }
        } catch (err) {
            console.error('Error triggering FMP 1w collection:', err);
            setError(err?.response?.data?.error || err.message || 'Failed to trigger FMP 1w collection');
        } finally {
            setFmpCollect1wLoading(false);
        }
    };

    // Trigger ETF Data Collection (Alpha Vantage + Yahoo fallback for 1h/4h/1d/1w)
    const triggerETFDataCollection = async () => {
        try {
            setEtfCollectLoading(true);
            setError('');
            setSuccess('');
            const response = await axios.post(`${API_BASE_URL}/api/jobs/trigger`, {
                jobName: 'ETFDataCollection',
                triggerUser: user?.username || user?.email || 'admin'
            });
            if (response.data?.success) {
                setSuccess(response.data.data?.message || 'ETF Data Collection triggered successfully');
            } else {
                setError(response.data?.error || 'Failed to trigger ETF Data Collection');
            }
        } catch (err) {
            console.error('Error triggering ETF Data Collection:', err);
            setError(err?.response?.data?.error || err.message || 'Failed to trigger ETF Data Collection');
        } finally {
            setEtfCollectLoading(false);
        }
    };

    // Resend an AI query using a saved query PROMPT log file
    const resendAIQueryFromLog = async () => {
        if (!resendFilename) {
            setError('Please enter a query log file name to resend');
            return;
        }
        try {
            setError('');
            setSuccess('');
            setResendResult(null);
            setResendLoading(true);
            const payload = { logFileName: resendFilename.trim() };
            const overrideVal = parseFloat(resendPriceOverride);
            if (!Number.isNaN(overrideVal)) {
                payload.currentPriceOverride = overrideVal;
            }
            const response = await axios.post(`${API_BASE_URL}/api/ai-predictions/resend-from-log`, payload);
            if (response.data?.success) {
                setSuccess(`Resent AI query from log: ${resendFilename.trim()}`);
                setResendResult(response.data?.data || null);
            } else {
                setError(response.data?.error || 'Failed to resend AI query from log');
            }
        } catch (err) {
            console.error('Error resending AI query from log:', err);
            setError(err.response?.data?.error || err.message || 'Failed to resend AI query from log');
        } finally {
            setResendLoading(false);
        }
    };

    // Reprocess a single AI prediction log file
    const reprocessLogFile = async () => {
        if (!reprocessFilename) {
            setError('Please enter a log file name to reprocess');
            return;
        }
        try {
            setError('');
            setSuccess('');
            setReprocessResult(null);
            setReprocessLoading(true);
            const trimmed = reprocessFilename.trim();
            const response = await axios.post(`${API_BASE_URL}/api/ai-predictions/reprocess-log`, { filename: trimmed });
            if (response.data?.success) {
                setSuccess(`Reprocessed log: ${trimmed}`);
                setReprocessResult(response.data?.data || null);
            } else {
                setError(response.data?.error || 'Failed to reprocess log file');
            }
        } catch (err) {
            console.error('Error reprocessing log file:', err);
            setError(err.response?.data?.error || err.message || 'Failed to reprocess log file');
        } finally {
            setReprocessLoading(false);
        }
    };

    const triggerManualRescore = async () => {
        if (!rescoreSince) {
            setError('Please select a date/time for rescoring');
            return;
        }
        try {
            setRescoreLoading(true);
            setError('');
            setSuccess('');
            // Convert datetime-local to ISO if needed
            // Browsers give 'YYYY-MM-DDTHH:mm'; send as-is, backend accepts both ISO and MySQL-like
            const payload = { since: rescoreSince.replace('T', ' ') + ':00' };
            const response = await axios.post(`${API_BASE_URL}/api/jobs/scoring/rescore-since`, payload);
            if (response.data?.success) {
                const d = response.data.data || {};
                setSuccess(`Rescore complete: rescored ${d.rescored}/${d.selected} predictions since ${d.since}.`);
            } else {
                setError(response.data?.error || 'Failed to run manual rescore');
            }
        } catch (err) {
            console.error('Error triggering manual rescore:', err);
            setError(err?.response?.data?.error || err.message || 'Failed to run manual rescore');
        } finally {
            setRescoreLoading(false);
        }
    };

    // Screenshot & Video Automation Handlers
    const captureScreenshot = async () => {
        if (!screenshotUrl.trim()) {
            setError('Please enter a URL');
            return;
        }
        if (!screenshotAsset.trim()) {
            setError('Please enter an asset/subfolder name');
            return;
        }
        try {
            setScreenshotLoading(true);
            setError('');
            setSuccess('');
            const payload = {
                url: screenshotUrl,
                subfolder: screenshotAsset,
                format: 'png'
            };
            const response = await axios.post(`${API_BASE_URL}/api/screenshot/capture-url`, payload);
            if (response.data?.success) {
                setSuccess(`Screenshot captured: ${response.data.path}`);
                setVideoResult({ type: 'screenshot', data: response.data });
                setScreenshotUrl('');
                setScreenshotAsset('');
            } else {
                setError(response.data?.error || 'Failed to capture screenshot');
            }
        } catch (err) {
            console.error('Error capturing screenshot:', err);
            setError(err?.response?.data?.error || err.message || 'Failed to capture screenshot');
        } finally {
            setScreenshotLoading(false);
        }
    };

    const generateBtcVideo = async () => {
        if (!btcImages.trim()) {
            setError('Please enter image paths (comma-separated)');
            return;
        }
        try {
            setBtcVideoLoading(true);
            setError('');
            setSuccess('');
            const images = btcImages.split(',').map(img => img.trim()).filter(Boolean);
            const payload = { images, date: new Date().toISOString().slice(0, 10) };
            const response = await axios.post(`${API_BASE_URL}/api/video/generate/btc`, payload);
            if (response.data?.videoPath) {
                setSuccess(`BTC video generated: ${response.data.videoPath}`);
                setVideoResult({ type: 'btc_video', data: response.data });
                setVideoFilePath(response.data.videoPath);
                setBtcImages('');
            } else {
                setError(response.data?.error || 'Failed to generate BTC video');
            }
        } catch (err) {
            console.error('Error generating BTC video:', err);
            setError(err?.response?.data?.error || err.message || 'Failed to generate BTC video');
        } finally {
            setBtcVideoLoading(false);
        }
    };

    const generateBriefingVideo = async () => {
        if (!briefingImages.trim()) {
            setError('Please enter image paths (asset|path format)');
            return;
        }
        try {
            setBriefingVideoLoading(true);
            setError('');
            setSuccess('');
            const imagesByAsset = { ETH: [], XRP: [], SPY: [] };
            const lines = briefingImages.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
            for (const line of lines) {
                const [asset, imagePath] = line.split('|').map(s => s.trim());
                if (asset && imagePath && imagesByAsset[asset]) {
                    imagesByAsset[asset].push(imagePath);
                }
            }
            const payload = { imagesByAsset, date: new Date().toISOString().slice(0, 10) };
            const response = await axios.post(`${API_BASE_URL}/api/video/generate/briefing`, payload);
            if (response.data?.videoPath) {
                setSuccess(`Briefing video generated: ${response.data.videoPath}`);
                setVideoResult({ type: 'briefing_video', data: response.data });
                setVideoFilePath(response.data.videoPath);
                setBriefingImages('');
            } else {
                setError(response.data?.error || 'Failed to generate Briefing video');
            }
        } catch (err) {
            console.error('Error generating Briefing video:', err);
            setError(err?.response?.data?.error || err.message || 'Failed to generate Briefing video');
        } finally {
            setBriefingVideoLoading(false);
        }
    };

    const publishToYouTube = async () => {
        if (!videoFilePath.trim()) {
            setError('Please enter a video file path');
            return;
        }
        try {
            setPublishLoading(true);
            setError('');
            setSuccess('');
            const payload = {
                filePath: videoFilePath,
                title: `AI Forecast - ${new Date().toISOString().slice(0, 10)}`,
                description: 'Automated AI forecast video',
                privacyStatus: videoPrivacy,
                tags: ['AI', 'Forecast', 'Trading']
            };
            const response = await axios.post(`${API_BASE_URL}/api/video/publish`, payload);
            if (response.data?.success || response.data?.videoId) {
                setSuccess(`Video published to YouTube: ${response.data.videoId}`);
                setVideoResult({ type: 'youtube_publish', data: response.data });
                setVideoFilePath('');
            } else {
                setError(response.data?.error || 'Failed to publish to YouTube');
            }
        } catch (err) {
            console.error('Error publishing to YouTube:', err);
            setError(err?.response?.data?.error || err.message || 'Failed to publish to YouTube');
        } finally {
            setPublishLoading(false);
        }
    };

    const runFullAutomation = async () => {
        if (!automationDateLabel.trim()) {
            setError('Please enter a date label (YYYY-MM-DD)');
            return;
        }
        try {
            setAutomationLoading(true);
            setError('');
            setSuccess('');
            const payload = { dateLabel: automationDateLabel };
            const response = await axios.post(`${API_BASE_URL}/api/jobs/run-video-automation`, payload);
            if (response.data?.success) {
                setSuccess(`Full automation completed for ${automationDateLabel}`);
                setVideoResult({ type: 'full_automation', data: response.data });
                setAutomationDateLabel('');
            } else {
                setError(response.data?.error || 'Failed to run full automation');
            }
        } catch (err) {
            console.error('Error running full automation:', err);
            setError(err?.response?.data?.error || err.message || 'Failed to run full automation');
        } finally {
            setAutomationLoading(false);
        }
    };

    const runDailyScreenshots = async () => {
        if (!screenshotsDateLabel.trim()) {
            setError('Please enter a date label (YYYY-MM-DD)');
            return;
        }
        try {
            setScreenshotsLoading(true);
            setError('');
            setSuccess('');
            const payload = { dateLabel: screenshotsDateLabel };
            const response = await axios.post(`${API_BASE_URL}/api/jobs/run-report-screenshots`, payload);
            if (response.data?.success) {
                setSuccess(`Daily screenshots captured for ${screenshotsDateLabel}`);
                setVideoResult({ type: 'daily_screenshots', data: response.data });
                setScreenshotsDateLabel('');
            } else {
                setError(response.data?.error || 'Failed to capture daily screenshots');
            }
        } catch (err) {
            console.error('Error running daily screenshots:', err);
            setError(err?.response?.data?.error || err.message || 'Failed to capture daily screenshots');
        } finally {
            setScreenshotsLoading(false);
        }
    };

    const runTimeframeScreenshots = async (timeframe) => {
        if (!screenshotsDateLabel.trim()) {
            setError('Please enter a date label (YYYY-MM-DD)');
            return;
        }
        try {
            // Set the correct loading state based on timeframe
            if (timeframe === '4h') setTimeframeLoading4h(true);
            else if (timeframe === '7d') setTimeframeLoading7d(true);
            else if (timeframe === '30d') setTimeframeLoading30d(true);
            else if (timeframe === '180d') setTimeframeLoading180d(true);
            
            setError('');
            setSuccess('');
            const payload = { timeframe, dateLabel: screenshotsDateLabel };
            const response = await axios.post(`${API_BASE_URL}/api/jobs/run-timeframe-screenshots`, payload);
            if (response.data?.success) {
                setSuccess(`${timeframe} screenshots captured for ${screenshotsDateLabel}`);
                setVideoResult({ type: 'timeframe_screenshots', timeframe, data: response.data });
            } else {
                setError(response.data?.error || `Failed to capture ${timeframe} screenshots`);
            }
        } catch (err) {
            console.error(`Error running ${timeframe} screenshots:`, err);
            setError(err?.response?.data?.error || err.message || `Failed to capture ${timeframe} screenshots`);
        } finally {
            // Clear the correct loading state
            if (timeframe === '4h') setTimeframeLoading4h(false);
            else if (timeframe === '7d') setTimeframeLoading7d(false);
            else if (timeframe === '30d') setTimeframeLoading30d(false);
            else if (timeframe === '180d') setTimeframeLoading180d(false);
        }
    };

    // Prediction Job Trigger Handler
    const triggerPredictionJob = async (type) => {
        setError('');
        setSuccess('');
        let endpoint = '';
        switch (type) {
            case 'daily': endpoint = '/api/cron/trigger/daily-predictions'; break;
            case '7d': endpoint = '/api/cron/trigger/weekly-7d-predictions'; break;
            case '30d': endpoint = '/api/cron/trigger/monthly-30d-predictions'; break;
            case '180d': endpoint = '/api/cron/trigger/monthly-180d-predictions'; break;
            default: return;
        }
        try {
            const response = await axios.post(`${API_BASE_URL}${endpoint}`);
            if (response.data?.success) {
                setSuccess(response.data.message || 'Prediction job triggered successfully.');
            } else {
                setError(response.data?.error || 'Failed to trigger prediction job.');
            }
        } catch (err) {
            setError(err?.response?.data?.error || err.message || 'Failed to trigger prediction job.');
        }
    };

    const processPerformance = async () => {
        setError('');
        setSuccess('');
        setProcessPerformanceLoading(true);
        try {
            const response = await axios.post(`${API_BASE_URL}/api/jobs/process-performance`);
            if (response.data?.success) {
                setSuccess(response.data.data?.message || 'Performance processing triggered successfully.');
            } else {
                setError(response.data?.error || 'Failed to process performance.');
            }
        } catch (err) {
            setError(err?.response?.data?.error || err.message || 'Failed to process performance.');
        } finally {
            setProcessPerformanceLoading(false);
        }
    };

    const processScore = async () => {
        setError('');
        setSuccess('');
        setProcessScoreLoading(true);
        try {
            const response = await axios.post(`${API_BASE_URL}/api/jobs/trigger`, {
                jobName: 'ExpiredPredictionScoring',
                triggerUser: user?.username || user?.email || 'admin'
            });
            if (response.data?.success) {
                setSuccess(response.data.data?.message || 'Score processing triggered successfully.');
            } else {
                setError(response.data?.error || 'Failed to process score.');
            }
        } catch (err) {
            setError(err?.response?.data?.error || err.message || 'Failed to process score.');
        } finally {
            setProcessScoreLoading(false);
        }
    };

    const triggerDailyCryptoPredictions = async () => {
        setError('');
        setSuccess('');
        try {
            const response = await axios.post(`${API_BASE_URL}/api/jobs/trigger`, {
                jobName: 'DailyCryptoPredictions',
                triggerUser: user?.username || user?.email || 'admin'
            });
            if (response.data?.success) {
                setSuccess(response.data.data?.message || 'Daily Crypto Predictions job triggered successfully.');
            } else {
                setError(response.data?.error || 'Failed to trigger Daily Crypto Predictions job.');
            }
        } catch (err) {
            setError(err?.response?.data?.error || err.message || 'Failed to trigger Daily Crypto Predictions job.');
        }
    };

    const triggerNonCryptoDailyPredictions = async () => {
        setError('');
        setSuccess('');
        const jobs = [
            'DailyETFPredictions',
            'DailyStockPredictionsSet1',
            'DailyStockPredictionsSet2'
        ];
        const triggerUser = user?.username || user?.email || 'admin';
        let successCount = 0;
        let failedJobs = [];

        for (const jobName of jobs) {
            try {
                const response = await axios.post(`${API_BASE_URL}/api/jobs/trigger`, {
                    jobName,
                    triggerUser
                });
                if (response.data?.success) {
                    successCount++;
                } else {
                    failedJobs.push(jobName);
                }
            } catch (err) {
                failedJobs.push(jobName);
            }
        }

        if (successCount === jobs.length) {
            setSuccess('All non-crypto daily prediction jobs triggered successfully.');
        } else if (successCount > 0) {
            setSuccess(`${successCount}/${jobs.length} non-crypto daily prediction jobs triggered. Failed: ${failedJobs.join(', ')}`);
        } else {
            setError(`Failed to trigger non-crypto daily prediction jobs: ${failedJobs.join(', ')}`);
        }
    };

    const getStatusBadge = (status) => {
        const colors = {
            'running': 'primary',
            'succeeded': 'success',
            'completed': 'success',
            'failed': 'danger'
        };
        return <Badge color={colors[status] || 'secondary'}>{status}</Badge>;
    };

    // Remove the old formatDuration and formatDate functions since we're using the utilities now

    // Admin access check
    if (!isAdmin) {
        return (
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Job Management" pageTitle="System" />
                    <Row>
                        <Col xl={12}>
                            <Card>
                                <CardBody>
                                    <div className="text-center">
                                        <i className="ri-shield-cross-line display-3 text-danger mb-3"></i>
                                        <h4 className="text-danger">Access Denied</h4>
                                        <p className="text-muted">You don't have permission to access this page. Only super users can manage jobs.</p>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Job Management" pageTitle="System" />
                    <Row>
                        <Col xl={12}>
                            <Card>
                                <CardBody>
                                    <div className="text-center">
                                        <Spinner color="primary" />
                                        <p className="mt-3">Loading job data...</p>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb title="Job Management" pageTitle="System" />
                
                {/* Alerts */}
                {error && (
                    <Alert color="danger" isOpen={!!error} toggle={() => setError('')} fade={false}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert color="success" isOpen={!!success} toggle={() => setSuccess('')} fade={false}>
                        {success}
                    </Alert>
                )}

                {/* Job Status Overview */}
                <Row>
                    <Col xl={3} md={6}>
                        <Card className="card-animate">
                            <CardBody>
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1 overflow-hidden">
                                        <p className="text-uppercase fw-medium text-muted text-truncate mb-0">
                                            Total Jobs
                                        </p>
                                    </div>
                                    <div className="flex-shrink-0">
                                        <h5 className="text-success fs-14 mb-0">
                                            <i className="ri-settings-2-line fs-13 align-middle"></i>
                                        </h5>
                                    </div>
                                </div>
                                <div className="d-flex align-items-end justify-content-between mt-4">
                                    <div>
                                        <h4 className="fs-22 fw-semibold ff-secondary mb-4">
                                            <CountUp
                                                start={0}
                                                end={jobStatus?.totalJobs || 0}
                                                duration={0.6}
                                                separator=","
                                            />
                                        </h4>
                                        <Badge color={jobStatus?.isRunning ? 'success' : 'danger'}>
                                            {jobStatus?.isRunning ? 'Running' : 'Stopped'}
                                        </Badge>
                                    </div>
                                    <div className="avatar-sm flex-shrink-0">
                                        <span className="avatar-title bg-success-subtle rounded fs-3">
                                            <i className="bx bx-cog text-success"></i>
                                        </span>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                    <Col xl={3} md={6}>
                        <Card className="card-animate">
                            <CardBody>
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1 overflow-hidden">
                                        <p className="text-uppercase fw-medium text-muted text-truncate mb-0">
                                            Recent Executions
                                        </p>
                                    </div>
                                    <div className="flex-shrink-0">
                                        <h5 className="text-success fs-14 mb-0">
                                            <i className="ri-history-line fs-13 align-middle"></i>
                                        </h5>
                                    </div>
                                </div>
                                <div className="d-flex align-items-end justify-content-between mt-4">
                                    <div>
                                        <h4 className="fs-22 fw-semibold ff-secondary mb-4">
                                            <CountUp
                                                start={0}
                                                end={jobExecutions?.length || 0}
                                                duration={0.6}
                                                separator=","
                                            />
                                        </h4>
                                        <span className="text-muted">Last 20 executions</span>
                                    </div>
                                    <div className="avatar-sm flex-shrink-0">
                                        <span className="avatar-title bg-info-subtle rounded fs-3">
                                            <i className="bx bx-time text-info"></i>
                                        </span>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                    {/* Manual Prediction Job Triggers */}
                    <Row className="mt-3">
                        <Col xl={12}>
                            <Card>
                                <CardHeader>
                                    <h4 className="card-title mb-0">Manual Prediction Job Triggers</h4>
                                </CardHeader>
                                <CardBody>
                                    <div className="d-flex gap-2 flex-wrap">
                                        <Button
                                            color="primary"
                                            onClick={() => triggerDailyCryptoPredictions()}
                                            disabled={false}
                                        >
                                            <i className="ri-calendar-2-line me-1"></i> Crypto Daily Predictions
                                        </Button>
                                        <Button
                                            color="success"
                                            onClick={() => triggerNonCryptoDailyPredictions()}
                                            disabled={false}
                                        >
                                            <i className="ri-calendar-2-line me-1"></i> Non-crypto Daily Predictions
                                        </Button>
                                        <Button
                                            color="secondary"
                                            onClick={processScore}
                                            disabled={processScoreLoading}
                                        >
                                            {processScoreLoading ? (
                                                <Spinner size="sm" className="me-2" />
                                            ) : (
                                                <i className="ri-scales-3-line me-1"></i>
                                            )}
                                            Process Score
                                        </Button>
                                        <Button
                                            color="secondary"
                                            onClick={processPerformance}
                                            disabled={processPerformanceLoading}
                                        >
                                            {processPerformanceLoading ? (
                                                <Spinner size="sm" className="me-2" />
                                            ) : (
                                                <i className="ri-bar-chart-2-line me-1"></i>
                                            )}
                                            Process Performance
                                        </Button>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Screenshot & Video Automation Services */}
                    <Row className="mt-3">
                        <Col xl={12}>
                            <Card>
                                <CardHeader className="d-flex justify-content-between align-items-center">
                                    <h4 className="card-title mb-0">Screenshot & Video Automation</h4>
                                </CardHeader>
                                <CardBody>
                                    <p className="text-muted mb-3">
                                        Manually trigger screenshot capture, video generation, and YouTube upload services.
                                    </p>
                                    <Row className="g-3">
                                        {/* Screenshot Capture */}
                                        <Col md={6} lg={4}>
                                            <Card className="border">
                                                <CardBody>
                                                    <h6 className="card-title">
                                                        <i className="bx bx-camera text-info"></i> Capture Screenshot
                                                    </h6>
                                                    <p className="text-muted small mb-2">
                                                        Capture screenshots from URL for specified asset and timeframe.
                                                    </p>
                                                    <FormGroup>
                                                        <Label className="small">URL</Label>
                                                        <Input
                                                            type="text"
                                                            placeholder="https://example.com"
                                                            className="form-control-sm"
                                                            value={screenshotUrl}
                                                            onChange={(e) => setScreenshotUrl(e.target.value)}
                                                        />
                                                    </FormGroup>
                                                    <FormGroup>
                                                        <Label className="small">Asset / Subfolder</Label>
                                                        <Input
                                                            type="text"
                                                            placeholder="BTC, ETH, etc."
                                                            className="form-control-sm"
                                                            value={screenshotAsset}
                                                            onChange={(e) => setScreenshotAsset(e.target.value)}
                                                        />
                                                    </FormGroup>
                                                    <Button
                                                        size="sm"
                                                        color="info"
                                                        className="w-100"
                                                        onClick={captureScreenshot}
                                                        disabled={screenshotLoading}
                                                    >
                                                        {screenshotLoading ? <Spinner size="sm" /> : (<><i className="bx bx-camera"></i> Capture</>)}
                                                    </Button>
                                                </CardBody>
                                            </Card>
                                        </Col>

                                        {/* Generate BTC Video */}
                                        <Col md={6} lg={4}>
                                            <Card className="border">
                                                <CardBody>
                                                    <h6 className="card-title">
                                                        <i className="bx bx-video text-warning"></i> Generate BTC Video
                                                    </h6>
                                                    <p className="text-muted small mb-2">
                                                        Create a BTC public video with screenshot images and AI narration.
                                                    </p>
                                                    <FormGroup>
                                                        <Label className="small">Image Paths (comma-separated)</Label>
                                                        <Input
                                                            type="textarea"
                                                            rows="2"
                                                            placeholder="/path/to/image1.png, /path/to/image2.png"
                                                            className="form-control-sm"
                                                            value={btcImages}
                                                            onChange={(e) => setBtcImages(e.target.value)}
                                                        />
                                                    </FormGroup>
                                                    <Button
                                                        size="sm"
                                                        color="warning"
                                                        className="w-100"
                                                        onClick={generateBtcVideo}
                                                        disabled={btcVideoLoading}
                                                    >
                                                        {btcVideoLoading ? <Spinner size="sm" /> : (<><i className="bx bx-video"></i> Generate Video</>)}
                                                    </Button>
                                                </CardBody>
                                            </Card>
                                        </Col>

                                        {/* Generate Briefing Video */}
                                        <Col md={6} lg={4}>
                                            <Card className="border">
                                                <CardBody>
                                                    <h6 className="card-title">
                                                        <i className="bx bx-video text-primary"></i> Generate Briefing Video
                                                    </h6>
                                                    <p className="text-muted small mb-2">
                                                        Create an ETH/XRP/SPY private briefing video with narration.
                                                    </p>
                                                    <FormGroup>
                                                        <Label className="small">Assets (ETH, XRP, SPY)</Label>
                                                        <Input
                                                            type="textarea"
                                                            rows="2"
                                                            placeholder="ETH|/path/to/eth1.png&#10;XRP|/path/to/xrp1.png&#10;SPY|/path/to/spy1.png"
                                                            className="form-control-sm"
                                                            value={briefingImages}
                                                            onChange={(e) => setBriefingImages(e.target.value)}
                                                        />
                                                    </FormGroup>
                                                    <Button
                                                        size="sm"
                                                        color="primary"
                                                        className="w-100"
                                                        onClick={generateBriefingVideo}
                                                        disabled={briefingVideoLoading}
                                                    >
                                                        {briefingVideoLoading ? <Spinner size="sm" /> : (<><i className="bx bx-video"></i> Generate Video</>)}
                                                    </Button>
                                                </CardBody>
                                            </Card>
                                        </Col>

                                        {/* Publish to YouTube */}
                                        <Col md={6} lg={4}>
                                            <Card className="border">
                                                <CardBody>
                                                    <h6 className="card-title">
                                                        <i className="bx bxl-youtube text-danger"></i> Publish to YouTube
                                                    </h6>
                                                    <p className="text-muted small mb-2">
                                                        Upload generated video to YouTube with metadata.
                                                    </p>
                                                    <FormGroup>
                                                        <Label className="small">Video File Path</Label>
                                                        <Input
                                                            type="text"
                                                            placeholder="/path/to/video.mp4"
                                                            className="form-control-sm"
                                                            value={videoFilePath}
                                                            onChange={(e) => setVideoFilePath(e.target.value)}
                                                        />
                                                    </FormGroup>
                                                    <FormGroup>
                                                        <Label className="small">Privacy</Label>
                                                        <Input
                                                            type="select"
                                                            className="form-control-sm"
                                                            value={videoPrivacy}
                                                            onChange={(e) => setVideoPrivacy(e.target.value)}
                                                        >
                                                            <option value="public">public</option>
                                                            <option value="private">private</option>
                                                        </Input>
                                                    </FormGroup>
                                                    <Button
                                                        size="sm"
                                                        color="danger"
                                                        className="w-100"
                                                        onClick={publishToYouTube}
                                                        disabled={publishLoading}
                                                    >
                                                        {publishLoading ? <Spinner size="sm" /> : (<><i className="bx bxl-youtube"></i> Publish</>)}
                                                    </Button>
                                                </CardBody>
                                            </Card>
                                        </Col>

                                        {/* Run Full Daily Automation */}
                                        <Col md={6} lg={4}>
                                            <Card className="border">
                                                <CardBody>
                                                    <h6 className="card-title">
                                                        <i className="bx bx-play-circle text-success"></i> Run Full Automation
                                                    </h6>
                                                    <p className="text-muted small mb-2">
                                                        Execute the complete daily video automation workflow (all steps).
                                                    </p>
                                                    <FormGroup>
                                                        <Label className="small">Date Label (YYYY-MM-DD)</Label>
                                                        <Input
                                                            type="date"
                                                            className="form-control-sm"
                                                            value={automationDateLabel}
                                                            onChange={(e) => setAutomationDateLabel(e.target.value)}
                                                        />
                                                    </FormGroup>
                                                    <Button
                                                        size="sm"
                                                        color="success"
                                                        className="w-100"
                                                        onClick={runFullAutomation}
                                                        disabled={automationLoading}
                                                    >
                                                        {automationLoading ? <Spinner size="sm" /> : (<><i className="bx bx-play-circle"></i> Run All</>)}
                                                    </Button>
                                                </CardBody>
                                            </Card>
                                        </Col>

                                        {/* Run Daily Report Screenshots */}
                                        <Col md={6} lg={4}>
                                            <Card className="border">
                                                <CardBody>
                                                    <h6 className="card-title">
                                                        <i className="bx bx-images text-secondary"></i> Daily Screenshots (1d)
                                                    </h6>
                                                    <p className="text-muted small mb-2">
                                                        Capture daily report screenshots for all assets.
                                                    </p>
                                                    <FormGroup>
                                                        <Label className="small">Date Label (YYYY-MM-DD)</Label>
                                                        <Input
                                                            type="date"
                                                            className="form-control-sm"
                                                            value={screenshotsDateLabel}
                                                            onChange={(e) => setScreenshotsDateLabel(e.target.value)}
                                                        />
                                                    </FormGroup>
                                                    <Button
                                                        size="sm"
                                                        color="secondary"
                                                        className="w-100"
                                                        onClick={runDailyScreenshots}
                                                        disabled={screenshotsLoading}
                                                    >
                                                        {screenshotsLoading ? <Spinner size="sm" /> : (<><i className="bx bx-images"></i> Capture 1d</>)}
                                                    </Button>
                                                </CardBody>
                                            </Card>
                                        </Col>

                                        {/* Timeframe Screenshots 4h */}
                                        <Col md={6} lg={4}>
                                            <Card className="border">
                                                <CardBody>
                                                    <h6 className="card-title">
                                                        <i className="bx bx-timer text-info"></i> 4-Hour Screenshots
                                                    </h6>
                                                    <p className="text-muted small mb-2">
                                                        Generate 4-hour timeframe videos for all assets.
                                                    </p>
                                                    <FormGroup>
                                                        <Label className="small">Date Label (YYYY-MM-DD)</Label>
                                                        <Input
                                                            type="date"
                                                            className="form-control-sm"
                                                            value={screenshotsDateLabel}
                                                            onChange={(e) => setScreenshotsDateLabel(e.target.value)}
                                                        />
                                                    </FormGroup>
                                                    <Button
                                                        size="sm"
                                                        color="info"
                                                        className="w-100"
                                                        onClick={() => runTimeframeScreenshots('4h')}
                                                        disabled={timeframeLoading4h}
                                                    >
                                                        {timeframeLoading4h ? <Spinner size="sm" /> : (<><i className="bx bx-video"></i> Capture 4h</>)}
                                                    </Button>
                                                </CardBody>
                                            </Card>
                                        </Col>

                                        {/* Timeframe Screenshots 7d */}
                                        <Col md={6} lg={4}>
                                            <Card className="border">
                                                <CardBody>
                                                    <h6 className="card-title">
                                                        <i className="bx bx-calendar-week text-primary"></i> 7-Day Screenshots
                                                    </h6>
                                                    <p className="text-muted small mb-2">
                                                        Generate 7-day timeframe videos for all assets.
                                                    </p>
                                                    <FormGroup>
                                                        <Label className="small">Date Label (YYYY-MM-DD)</Label>
                                                        <Input
                                                            type="date"
                                                            className="form-control-sm"
                                                            value={screenshotsDateLabel}
                                                            onChange={(e) => setScreenshotsDateLabel(e.target.value)}
                                                        />
                                                    </FormGroup>
                                                    <Button
                                                        size="sm"
                                                        color="primary"
                                                        className="w-100"
                                                        onClick={() => runTimeframeScreenshots('7d')}
                                                        disabled={timeframeLoading7d}
                                                    >
                                                        {timeframeLoading7d ? <Spinner size="sm" /> : (<><i className="bx bx-video"></i> Capture 7d</>)}
                                                    </Button>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    </Row>

                                    {/* Second Row - 30d and 180d */}
                                    <Row className="g-3 mt-1">
                                        {/* Timeframe Screenshots 30d */}
                                        <Col md={6} lg={4}>
                                            <Card className="border">
                                                <CardBody>
                                                    <h6 className="card-title">
                                                        <i className="bx bx-calendar text-warning"></i> 30-Day Screenshots
                                                    </h6>
                                                    <p className="text-muted small mb-2">
                                                        Generate 30-day timeframe videos for all assets.
                                                    </p>
                                                    <FormGroup>
                                                        <Label className="small">Date Label (YYYY-MM-DD)</Label>
                                                        <Input
                                                            type="date"
                                                            className="form-control-sm"
                                                            value={screenshotsDateLabel}
                                                            onChange={(e) => setScreenshotsDateLabel(e.target.value)}
                                                        />
                                                    </FormGroup>
                                                    <Button
                                                        size="sm"
                                                        color="warning"
                                                        className="w-100"
                                                        onClick={() => runTimeframeScreenshots('30d')}
                                                        disabled={timeframeLoading30d}
                                                    >
                                                        {timeframeLoading30d ? <Spinner size="sm" /> : (<><i className="bx bx-video"></i> Capture 30d</>)}
                                                    </Button>
                                                </CardBody>
                                            </Card>
                                        </Col>

                                        {/* Timeframe Screenshots 180d */}
                                        <Col md={6} lg={4}>
                                            <Card className="border">
                                                <CardBody>
                                                    <h6 className="card-title">
                                                        <i className="bx bx-calendar-event text-danger"></i> 180-Day Screenshots
                                                    </h6>
                                                    <p className="text-muted small mb-2">
                                                        Generate 180-day timeframe videos for all assets.
                                                    </p>
                                                    <FormGroup>
                                                        <Label className="small">Date Label (YYYY-MM-DD)</Label>
                                                        <Input
                                                            type="date"
                                                            className="form-control-sm"
                                                            value={screenshotsDateLabel}
                                                            onChange={(e) => setScreenshotsDateLabel(e.target.value)}
                                                        />
                                                    </FormGroup>
                                                    <Button
                                                        size="sm"
                                                        color="danger"
                                                        className="w-100"
                                                        onClick={() => runTimeframeScreenshots('180d')}
                                                        disabled={timeframeLoading180d}
                                                    >
                                                        {timeframeLoading180d ? <Spinner size="sm" /> : (<><i className="bx bx-video"></i> Capture 180d</>)}
                                                    </Button>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Resend AI Query From Log (PROMPT) */}
                    <Row className="mt-3">
                        <Col xl={12}>
                            <Card>
                                <CardHeader className="d-flex justify-content-between align-items-center">
                                    <h4 className="card-title mb-0">Resend AI Query From Log</h4>
                                </CardHeader>
                                <CardBody>
                                    <p className="text-muted">
                                        Use a saved AI <strong>query log</strong> (PROMPT) file to resend the request to the model and save a new prediction.
                                        Enter only the file name (e.g., <code>2025-09-08-22-39-59-ETH-gpt-5-mini-4hours-with-TA-query.txt</code>). The server will locate it.
                                    </p>
                                    <Row className="g-3 align-items-end">
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label for="resendFilename">Query Log File Name</Label>
                                                <Input
                                                    id="resendFilename"
                                                    type="text"
                                                    placeholder="YYYY-MM-DD-HH-mm-ss-SYMBOL-MODEL-PERIOD-with-TA-query.txt"
                                                    value={resendFilename}
                                                    onChange={(e) => setResendFilename(e.target.value)}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={3}>
                                            <FormGroup>
                                                <Label for="resendPriceOverride">Current Price Override (optional)</Label>
                                                <Input
                                                    id="resendPriceOverride"
                                                    type="number"
                                                    step="0.01"
                                                    placeholder="e.g. 4366.17"
                                                    value={resendPriceOverride}
                                                    onChange={(e) => setResendPriceOverride(e.target.value)}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={3}>
                                            <Button color="primary" onClick={resendAIQueryFromLog} disabled={resendLoading || !resendFilename.trim()}>
                                                {resendLoading ? <Spinner size="sm" /> : (<><i className="ri-send-plane-2-line me-1"></i> Resend AI Query</>)}
                                            </Button>
                                        </Col>
                                    </Row>
                                    {resendResult && (
                                        <div className="mt-3">
                                            <h6 className="mb-2">Resend Result</h6>
                                            <pre className="bg-light p-3 rounded" style={{ whiteSpace: 'pre-wrap' }}>
                                                {JSON.stringify(resendResult, null, 2)}
                                            </pre>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    <Col xl={3} md={6}>
                        <Card className="card-animate">
                            <CardBody>
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1 overflow-hidden">
                                        <p className="text-uppercase fw-medium text-muted text-truncate mb-0">
                                            Success Rate
                                        </p>
                                    </div>
                                    <div className="flex-shrink-0">
                                        <h5 className="text-success fs-14 mb-0">
                                            <i className="ri-checkbox-circle-line fs-13 align-middle"></i>
                                        </h5>
                                    </div>
                                </div>
                                <div className="d-flex align-items-end justify-content-between mt-4">
                                    <div>
                                        <h4 className="fs-22 fw-semibold ff-secondary mb-4">
                                            <CountUp
                                                start={0}
                                                end={jobStatus?.statistics?.reduce((acc, stat) => {
                                                    const successRate = stat.total_executions > 0 ? 
                                                        (stat.successful / stat.total_executions) * 100 : 0;
                                                    return acc + successRate;
                                                }, 0) / (jobStatus?.statistics?.length || 1) || 0}
                                                duration={0.6}
                                                decimals={1}
                                                suffix="%"
                                            />
                                        </h4>
                                        <span className="text-muted">Overall success rate</span>
                                    </div>
                                    <div className="avatar-sm flex-shrink-0">
                                        <span className="avatar-title bg-success-subtle rounded fs-3">
                                            <i className="bx bx-check-circle text-success"></i>
                                        </span>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                    <Col xl={3} md={6}>
                        <Card className="card-animate">
                            <CardBody>
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1 overflow-hidden">
                                        <p className="text-uppercase fw-medium text-muted text-truncate mb-0">
                                            Average Duration
                                        </p>
                                    </div>
                                    <div className="flex-shrink-0">
                                        <h5 className="text-success fs-14 mb-0">
                                            <i className="ri-timer-line fs-13 align-middle"></i>
                                        </h5>
                                    </div>
                                </div>
                                <div className="d-flex align-items-end justify-content-between mt-4">
                                    <div>
                                        <h4 className="fs-22 fw-semibold ff-secondary mb-4">
                                            {jobStatus?.statistics?.reduce((acc, stat) => acc + (stat.avg_duration_seconds || 0), 0) / (jobStatus?.statistics?.length || 1) || 0}s
                                        </h4>
                                        <span className="text-muted">Average execution time</span>
                                    </div>
                                    <div className="avatar-sm flex-shrink-0">
                                        <span className="avatar-title bg-warning-subtle rounded fs-3">
                                            <i className="bx bx-stopwatch text-warning"></i>
                                        </span>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Job Control Panel */}
                <Row>
                    <Col xl={6}>
                        <Card>
                            <CardHeader>
                                <h4 className="card-title mb-0">Job Control</h4>
                            </CardHeader>
                            <CardBody>
                                <div className="d-flex gap-2 mb-3">
                                    <Button 
                                        color="success" 
                                        onClick={() => controlScheduler('start')}
                                        disabled={jobStatus?.isRunning}
                                    >
                                        <i className="ri-play-line"></i> Start Scheduler
                                    </Button>
                                    <Button 
                                        color="danger" 
                                        onClick={() => controlScheduler('stop')}
                                        disabled={!jobStatus?.isRunning}
                                    >
                                        <i className="ri-pause-line"></i> Stop Scheduler
                                    </Button>
                                    <Button 
                                        color="warning" 
                                        onClick={() => controlScheduler('restart')}
                                    >
                                        <i className="ri-refresh-line"></i> Restart Scheduler
                                    </Button>
                                </div>
                                <div className="d-flex gap-2 flex-wrap">
                                    <Button
                                        color="warning"
                                        onClick={triggerETFDataCollection}
                                        disabled={etfCollectLoading}
                                    >
                                        {etfCollectLoading ? (
                                            <Spinner size="sm" />
                                        ) : (
                                            <>
                                                <i className="ri-stock-line"></i> Refresh ETF Data (All Timeframes)
                                            </>
                                        )}
                                    </Button>
                                    <Button color="info" onClick={() => triggerJob('ShortTermPredictions')}>
                                        <i className="ri-calendar-2-line"></i> Run Daily Prediction Job
                                    </Button>
                                    <Button color="info" onClick={() => triggerJob('LongTermPredictions')}>
                                        <i className="ri-calendar-check-line"></i> Run 7 Day Prediction Job
                                    </Button>
                                    <Button color="info" onClick={() => triggerJob('LongTermPredictions')}>
                                        <i className="ri-calendar-event-line"></i> Run 30 Day Prediction Job
                                    </Button>
                                    <Button 
                                        color="primary" 
                                        onClick={() => setTriggerModal(true)}
                                    >
                                        <i className="ri-play-circle-line"></i> Trigger Manual Job
                                    </Button>
                                    <Button
                                        color="secondary"
                                        onClick={triggerFMPCollect1hAll}
                                        disabled={fmpCollect1hLoading}
                                    >
                                        {fmpCollect1hLoading ? (
                                            <Spinner size="sm" />
                                        ) : (
                                            <>
                                                <i className="ri-time-line"></i> Run 1h FMP Collection (All Symbols)
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        color="secondary"
                                        onClick={triggerFMPCollect1dAll}
                                        disabled={fmpCollect1dLoading}
                                    >
                                        {fmpCollect1dLoading ? (
                                            <Spinner size="sm" />
                                        ) : (
                                            <>
                                                <i className="ri-calendar-2-line"></i> Run 1d FMP Collection (All Symbols)
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        color="secondary"
                                        onClick={triggerFMPCollect1wAll}
                                        disabled={fmpCollect1wLoading}
                                    >
                                        {fmpCollect1wLoading ? (
                                            <Spinner size="sm" />
                                        ) : (
                                            <>
                                                <i className="ri-calendar-check-line"></i> Run 1w FMP Collection (All Symbols)
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        color="secondary"
                                        onClick={triggerFMPCollect4hAll}
                                        disabled={fmpCollectLoading}
                                    >
                                        {fmpCollectLoading ? (
                                            <Spinner size="sm" />
                                        ) : (
                                            <>
                                                <i className="ri-database-2-line"></i> Run 4h FMP Collection (All Symbols)
                                            </>
                                        )}
                                    </Button>
                                    <Button 
                                        color="secondary" 
                                        onClick={loadJobData}
                                    >
                                        <i className="ri-refresh-line"></i> Refresh Data
                                    </Button>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                    <Col xl={6}>
                        <Card>
                            <CardHeader>
                                <h4 className="card-title mb-0">Scheduled Jobs</h4>
                            </CardHeader>
                            <CardBody>
                                <div className="table-responsive">
                                    <Table className="table-nowrap mb-0">
                                        <thead>
                                            <tr>
                                                <th>Job Name</th>
                                                <th>Schedule</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {jobStatus?.jobs?.map((job, index) => (
                                                <tr key={index}>
                                                    <td>{job.name}</td>
                                                    <td>{job.schedule}</td>
                                                    <td>
                                                        <Badge color={job.isRunning ? 'success' : 'secondary'}>
                                                            {job.isRunning ? 'Active' : 'Inactive'}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Prediction Recovery / Log Reprocess */}
                <Row className="mt-3">
                    <Col xl={12}>
                        <Card>
                            <CardHeader>
                                <h4 className="card-title mb-0">Prediction Recovery</h4>
                            </CardHeader>
                            <CardBody>
                                <p className="text-muted" style={{ fontSize: '0.85rem' }}>
                                    Reprocess a single AI prediction log file (previously stored raw model response). Enter only the file name (not the full path). The backend will parse and save the prediction if valid and not a duplicate.
                                </p>
                                <div className="d-flex flex-column flex-md-row gap-2">
                                    <Input
                                        type="text"
                                        placeholder="example: 2024-09-05_12-00-00_BTC_gpt_4h.log"
                                        value={reprocessFilename}
                                        onChange={(e) => setReprocessFilename(e.target.value)}
                                    />
                                    <Button color="info" onClick={reprocessLogFile} disabled={reprocessLoading || !reprocessFilename.trim()}>
                                        {reprocessLoading ? <Spinner size="sm" /> : <><i className="ri-upload-cloud-line me-1"></i> Reprocess Log</>}
                                    </Button>
                                </div>
                                {reprocessResult && (
                                    <div className="mt-3" style={{ fontSize: '0.75rem' }}>
                                        <h6 className="mb-1">Result</h6>
                                        <pre style={{ maxHeight: '200px', overflow: 'auto', background: '#f8f9fa', padding: '8px', borderRadius: '4px' }}>
{JSON.stringify(reprocessResult, null, 2)}
                                        </pre>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Debug Information */}
                <Row>
                    <Col xl={12}>
                        <Card>
                            <CardHeader>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h4 className="card-title mb-0">Debug Information</h4>
                                    <div>
                                        <Button color="success" size="sm" onClick={testAPICall} className="me-2">
                                            Test API
                                        </Button>
                                        <Button color="primary" size="sm" onClick={loadJobData} className="me-2">
                                            Refresh Data
                                        </Button>
                                        <Button color="warning" size="sm" onClick={loadSimpleData}>
                                            Load Simple
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardBody>
                                <div style={{ fontSize: '12px', backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '4px' }}>
                                    <p><strong>User:</strong> {JSON.stringify(user)}</p>
                                    <p><strong>Is Admin:</strong> {isAdmin ? 'Yes' : 'No'}</p>
                                    <p><strong>API Base URL:</strong> {API_BASE_URL}</p>
                                    <p><strong>jobExecutions type:</strong> {typeof jobExecutions}</p>
                                    <p><strong>jobExecutions is array:</strong> {Array.isArray(jobExecutions) ? 'Yes' : 'No'}</p>
                                    <p><strong>jobExecutions length:</strong> {jobExecutions?.length || 0}</p>
                                    <p><strong>Loading state:</strong> {loading ? 'Yes' : 'No'}</p>
                                    <p><strong>Error:</strong> {error || 'None'}</p>
                                    <p><strong>Simple executions length:</strong> {simpleExecutions?.length || 0}</p>
                                    <p><strong>Raw data:</strong></p>
                                    <pre style={{ maxHeight: '200px', overflow: 'auto' }}>
                                        {JSON.stringify(jobExecutions, null, 2)}
                                    </pre>
                                    <p><strong>Simple data:</strong></p>
                                    <pre style={{ maxHeight: '200px', overflow: 'auto' }}>
                                        {JSON.stringify(simpleExecutions, null, 2)}
                                    </pre>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Job Execution History */}
                <Row>
                    <Col xl={12}>
                        <Card>
                            <CardHeader>
                                <div className="d-flex justify-content-between align-items-center">
                                    <h4 className="card-title mb-0">Job Execution History</h4>
                                    <small className="text-muted">
                                        <i className="ri-time-zone-line me-1"></i>
                                        Times shown in your local timezone ({getTimezoneInfo().offset})
                                    </small>
                                </div>
                            </CardHeader>
                            <CardBody>
                                <div className="table-responsive">
                                    <Table className="table-nowrap mb-0">
                                        <thead>
                                            <tr>
                                                <th>Job Name</th>
                                                <th>Type</th>
                                                <th>Status</th>
                                                <th>Started</th>
                                                <th>Duration</th>
                                                <th>Triggered By</th>
                                                <th>User</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                console.log('🔍 Table rendering debug:');
                                                console.log('loading:', loading);
                                                console.log('jobExecutions:', jobExecutions);
                                                console.log('jobExecutions.length:', jobExecutions?.length);
                                                console.log('Array.isArray(jobExecutions):', Array.isArray(jobExecutions));
                                                console.log('First execution:', jobExecutions?.[0]);
                                                return null;
                                            })()}
                                            {!loading && jobExecutions?.length > 0 ? (
                                                jobExecutions.map((execution, index) => (
                                                    <tr key={execution.id || index}>
                                                        <td>{execution.job_name || 'N/A'}</td>
                                                        <td>
                                                            <Badge color="info">
                                                                {execution.job_type?.replace('_', ' ') || 'N/A'}
                                                            </Badge>
                                                        </td>
                                                        <td>{getStatusBadge(execution.status)}</td>
                                                        <td>{formatDateTime(execution.started_at)}</td>
                                                        <td>{formatDuration(execution.duration_seconds)}</td>
                                                        <td>
                                                            <Badge color={execution.trigger_user ? 'warning' : 'primary'}>
                                                                {execution.trigger_user ? 'manual' : 'scheduled'}
                                                            </Badge>
                                                        </td>
                                                        <td>{execution.trigger_user || 'System'}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="7" className="text-center text-muted py-4">
                                                        {loading ? 'Loading...' : 'No job executions found'}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Manual Job Trigger Modal */}
                <Modal isOpen={triggerModal} toggle={closeTriggerModal} centered>
                    <ModalHeader toggle={closeTriggerModal}>
                        Trigger Manual Job
                    </ModalHeader>
                    <ModalBody>
                        <Form>
                            <FormGroup>
                                <Label for="jobSelect">Select Job</Label>
                                <Input 
                                    type="select" 
                                    id="jobSelect"
                                    value={selectedJob}
                                    onChange={(e) => setSelectedJob(e.target.value)}
                                >
                                    <option value="">Select a job...</option>
                                    <optgroup label="Scoring & Core Jobs">
                                        <option value="ExpiredPredictionScoring">Expired Prediction Scoring</option>
                                        <option value="ShortTermPredictions">Short Term Predictions (All Models & Timeframes)</option>
                                        <option value="LongTermPredictions">Long Term Predictions (All Models & Timeframes)</option>
                                    </optgroup>
                                    
                                    <optgroup label="Hourly ETF Predictions — TQQQ">
                                        <option value="HourlyTQQQPredictions">TQQQ All Models (1h)</option>
                                        <option value="HourlyTQQQPredictions_GPT">TQQQ — gpt (1h)</option>
                                        <option value="HourlyTQQQPredictions_Gemini">TQQQ — Gemini (1h)</option>
                                    </optgroup>
                                    
                                    <optgroup label="Hourly ETF Predictions — IBIT">
                                        <option value="HourlyIBITPredictions">IBIT All Models (1h)</option>
                                        <option value="HourlyIBITPredictions_GPT">IBIT — gpt (1h)</option>
                                        <option value="HourlyIBITPredictions_Gemini">IBIT — Gemini (1h)</option>
                                    </optgroup>
                                    
                                    <optgroup label="Hourly ETF Predictions — UGL">
                                        <option value="HourlyUGLPredictions">UGL All Models (1h)</option>
                                        <option value="HourlyUGLPredictions_GPT">UGL — gpt (1h)</option>
                                        <option value="HourlyUGLPredictions_Gemini">UGL — Gemini (1h)</option>
                                    </optgroup>
                                    
                                    <optgroup label="Hourly ETF Predictions — SOXL">
                                        <option value="HourlySoxlPredictions">SOXL All Models (1h)</option>
                                        <option value="HourlySoxlPredictions_GPT">SOXL — gpt (1h)</option>
                                        <option value="HourlySoxlPredictions_Gemini">SOXL — Gemini (1h)</option>
                                    </optgroup>
                                    
                                    <optgroup label="Hourly ETF Predictions — FAS">
                                        <option value="HourlyFasPredictions">FAS All Models (1h)</option>
                                        <option value="HourlyFasPredictions_GPT">FAS — gpt (1h)</option>
                                        <option value="HourlyFasPredictions_Gemini">FAS — Gemini (1h)</option>
                                    </optgroup>
                                    
                                    <optgroup label="Hourly ETF Predictions — TMF">
                                        <option value="HourlyTmfPredictions">TMF All Models (1h)</option>
                                        <option value="HourlyTmfPredictions_GPT">TMF — gpt (1h)</option>
                                        <option value="HourlyTmfPredictions_Gemini">TMF — Gemini (1h)</option>
                                    </optgroup>
                                    
                                    <optgroup label="Hourly ETF Predictions — LABU">
                                        <option value="HourlyLabuPredictions">LABU All Models (1h)</option>
                                        <option value="HourlyLabuPredictions_GPT">LABU — gpt (1h)</option>
                                        <option value="HourlyLabuPredictions_Gemini">LABU — Gemini (1h)</option>
                                    </optgroup>
                                    
                                    <optgroup label="Hourly ETF Predictions — UCO">
                                        <option value="HourlyUcoPredictions">UCO All Models (1h)</option>
                                        <option value="HourlyUcoPredictions_GPT">UCO — gpt (1h)</option>
                                        <option value="HourlyUcoPredictions_Gemini">UCO — Gemini (1h)</option>
                                    </optgroup>
                                    
                                    <optgroup label="Hourly ETF Predictions — HF">
                                        <option value="HourlyHFPrediction">HF All Models (1h)</option>
                                        <option value="HourlyHFPrediction_GPT">HF — gpt (1h)</option>
                                        <option value="HourlyHFPrediction_Gemini">HF — Gemini (1h)</option>
                                    </optgroup>
                                    
                                    <optgroup label="Testing">
                                        <option value="SpecificPrediction">Specific AI Model & Timeframe (Testing)</option>
                                    </optgroup>
                                </Input>
                            </FormGroup>
                            
                            {/* Symbol selection for prediction jobs */}
                            {isPredictionJob(selectedJob) && (
                                <>
                                    <FormGroup>
                                        <Label for="symbolSelect">Select Asset</Label>
                                        <Input 
                                            type="select" 
                                            id="symbolSelect"
                                            value={selectedSymbol}
                                            onChange={(e) => setSelectedSymbol(e.target.value)}
                                        >
                                            <option value="BTC">Bitcoin (BTC)</option>
                                            <option value="ETH">Ethereum (ETH)</option>
                                            <option value="XRP">XRP (XRP)</option>
                                            <option value="SOL">SOL (Solana)</option>
                                            <option value="PAXG">PAXG (Paxos Gold)</option>
                                            <option value="AVAX">AVAX (Avalanche)</option>
                                            <option value="ADA">ADA (Cardano)</option>
                                            <option value="ARB">ARB (Arbitrum)</option>
                                            <option value="LINK">LINK (Chainlink)</option>
                                            <option value="UNI">UNI (Uniswap)</option>
                                            <option value="DOGE">DOGE (Dogecoin)</option>
                                            <option value="SPY">SPDR S&amp;P 500 ETF (SPY)</option>
                                            <option value="TQQQ">ProShares UltraPro QQQ (TQQQ)</option>
                                            <option value="SOXL">Direxion Daily Semiconductors Bull 3X (SOXL)</option>
                                            <option value="IBIT">iShares Bitcoin Trust (IBIT)</option>
                                            <option value="FAS">Direxion Daily Financials Bull 3X (FAS)</option>
                                            <option value="LABU">Direxion Daily Biotech Bull 3X (LABU)</option>
                                            <option value="UCO">ProShares Ultra DJ-UBS Crude Oil (UCO)</option>
                                            <option value="GUSH">Direxion Oil &amp; Gas Bull ETF (GUSH)</option>
                                            <option value="TMF">Direxion 20+ Yr Treasury Bull 3X (TMF)</option>
                                            <option value="TNA">Direxion Small Cap Bull 3X (TNA)</option>
                                            <option value="UGL">ProShares Ultra Gold (UGL)</option>
                                            <option value="FTS">Fortis Inc (FTS)</option>
                                            <option value="WSP.TO">WSP Global Inc (WSP.TO)</option>
                                        </Input>
                                        <small className="text-muted">
                                            Generate predictions for the selected asset
                                        </small>
                                    </FormGroup>
                                    
                                    <FormGroup>
                                        <div className="form-check form-switch">
                                            <Input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="technicalIndicatorsSwitch"
                                                checked={includeTechnicalIndicators}
                                                onChange={(e) => setIncludeTechnicalIndicators(e.target.checked)}
                                            />
                                            <Label className="form-check-label" for="technicalIndicatorsSwitch">
                                                Include Technical Analysis
                                            </Label>
                                        </div>
                                        <small className="text-muted">
                                            {includeTechnicalIndicators ? 
                                                'Technical indicators (RSI, MACD, EMA) will be included in the analysis' :
                                                'Only price data will be used for predictions'
                                            }
                                        </small>
                                    </FormGroup>
                                </>
                            )}
                            
                            {/* Specific model and timeframe selection for SpecificPrediction */}
                            {selectedJob === 'SpecificPrediction' && (
                                <>
                                    <FormGroup>
                                        <Label for="modelSelect">Select AI Model</Label>
                                        <Input 
                                            type="select" 
                                            id="modelSelect"
                                            value={selectedModel}
                                            onChange={(e) => setSelectedModel(e.target.value)}
                                        >
                                            <option value="">Select AI model...</option>
                                            <optgroup label="Premium Models">
                                                <option value="gpt-premium">gpt-premium (OpenAI)</option>
                                                <option value="Gemini-premium">Gemini-premium (Google)</option>
                                                <option value="Claude-premium">Claude-premium (Anthropic)</option>
                                            </optgroup>
                                            <optgroup label="Cost-Effective Models">
                                                <option value="gpt">gpt (OpenAI)</option>
                                                <option value="Claude">Claude (Anthropic)</option>
                                                <option value="Gemini">Gemini (Google)</option>
                                            </optgroup>
                                            <optgroup label="Custom Models">
                                                <option value="BIS">BIS (Custom)</option>
                                                <option value="hf-v1">HF-v1 (Custom)</option>
                                            </optgroup>
                                        </Input>
                                        <small className="text-muted">
                                            Choose specific AI model for testing to save tokens
                                        </small>
                                    </FormGroup>
                                    
                                    <FormGroup>
                                        <Label for="timeframeSelect">Select Timeframe</Label>
                                        <Input 
                                            type="select" 
                                            id="timeframeSelect"
                                            value={selectedTimeframe}
                                            onChange={(e) => setSelectedTimeframe(e.target.value)}
                                        >
                                            <option value="">Select timeframe...</option>
                                            <optgroup label="Short-term Timeframes">
                                                <option value="4h">4 Hours</option>
                                                <option value="1d">1 Day</option>
                                                <option value="2d">2 Days</option>
                                                {(['BIS', 'hf-v1'].includes(selectedModel)) && <option value="1h">1 Hour</option>}
                                            </optgroup>
                                            <optgroup label="Long-term Timeframes">
                                                <option value="7d">7 Days (1 Week)</option>
                                                <option value="30d">30 Days (1 Month)</option>
                                                <option value="180d">180 Days (6 Months)</option>
                                            </optgroup>
                                        </Input>
                                        <small className="text-muted">
                                            Choose specific timeframe for focused testing
                                        </small>
                                    </FormGroup>
                                </>
                            )}
                        </Form>
                        
                        {/* Information about the selected job */}
                        {selectedJob && (
                            <div className="mt-3 p-3 bg-light rounded">
                                <small className="text-muted">
                                    <strong>About this job:</strong><br/>
                                    {selectedJob === 'ExpiredPredictionScoring' && 
                                        'Scores expired predictions and calculates model accuracy metrics.'
                                    }
                                    {selectedJob === 'ShortTermPredictions' && 
                                        `Generates AI predictions for ${selectedSymbol} covering 4-hour, 24-hour, and 48-hour timeframes${includeTechnicalIndicators ? ' with technical indicators (RSI, MACD, EMA)' : ' using price data only'}.`
                                    }
                                    {selectedJob === 'LongTermPredictions' && 
                                        `Generates AI predictions for ${selectedSymbol} covering 7-day, 30-day, and 180-day timeframes${includeTechnicalIndicators ? ' with technical indicators (RSI, MACD, EMA)' : ' using price data only'}.`
                                    }
                                    {selectedJob === 'SpecificPrediction' && 
                                        `Run a specific AI model (${selectedModel}) for a specific timeframe (${selectedTimeframe}) on ${selectedSymbol}.`
                                    }
                                    
                                    {/* TQQQ Hourly Predictions */}
                                    {selectedJob === 'HourlyTQQQPredictions' && 
                                        'Generates 1-hour price forecasts for TQQQ using both gpt and Claude. Uses short-term timeframe weighting (5m, 15m, 1h, 4h).'
                                    }
                                    {selectedJob === 'HourlyTQQQPredictions_GPT' && 
                                        'Generates a 1-hour price forecast for TQQQ using gpt only. Uses short-term timeframe weighting (5m, 15m, 1h, 4h).'
                                    }
                                    {selectedJob === 'HourlyTQQQPredictions_Gemini' && 
                                        'Generates a 1-hour price forecast for TQQQ using Gemini only. Uses short-term timeframe weighting (5m, 15m, 1h, 4h).'
                                    }
                                    
                                    {/* IBIT Hourly Predictions */}
                                    {selectedJob === 'HourlyIBITPredictions' && 
                                        'Generates 1-hour price forecasts for IBIT using both gpt and Claude. Uses short-term timeframe weighting (5m, 15m, 1h, 4h).'
                                    }
                                    {selectedJob === 'HourlyIBITPredictions_GPT' && 
                                        'Generates a 1-hour price forecast for IBIT using gpt only. Uses short-term timeframe weighting (5m, 15m, 1h, 4h).'
                                    }
                                    {selectedJob === 'HourlyIBITPredictions_Gemini' && 
                                        'Generates a 1-hour price forecast for IBIT using Gemini only. Uses short-term timeframe weighting (5m, 15m, 1h, 4h).'
                                    }
                                    
                                    {/* UGL Hourly Predictions */}
                                    {selectedJob === 'HourlyUGLPredictions' && 
                                        'Generates 1-hour price forecasts for UGL (inverse gold ETF) using both gpt and Claude. Uses short-term timeframe weighting.'
                                    }
                                    {selectedJob === 'HourlyUGLPredictions_GPT' && 
                                        'Generates a 1-hour price forecast for UGL using gpt only. Uses short-term timeframe weighting (5m, 15m, 1h, 4h).'
                                    }
                                    {selectedJob === 'HourlyUGLPredictions_Gemini' && 
                                        'Generates a 1-hour price forecast for UGL using Gemini only. Uses short-term timeframe weighting (5m, 15m, 1h, 4h).'
                                    }
                                    
                                    {/* SOXL Hourly Predictions */}
                                    {selectedJob === 'HourlySoxlPredictions' && 
                                        'Generates 1-hour price forecasts for SOXL (semiconductor bull ETF) using both gpt and Claude. Uses short-term timeframe weighting.'
                                    }
                                    {selectedJob === 'HourlySoxlPredictions_GPT' && 
                                        'Generates a 1-hour price forecast for SOXL using gpt only. Uses short-term timeframe weighting (5m, 15m, 1h, 4h).'
                                    }
                                    {selectedJob === 'HourlySoxlPredictions_Gemini' && 
                                        'Generates a 1-hour price forecast for SOXL using Gemini only. Uses short-term timeframe weighting (5m, 15m, 1h, 4h).'
                                    }
                                    
                                    {/* FAS Hourly Predictions */}
                                    {selectedJob === 'HourlyFasPredictions' && 
                                        'Generates 1-hour price forecasts for FAS (financial services bull ETF) using both gpt and Claude. Uses short-term timeframe weighting.'
                                    }
                                    {selectedJob === 'HourlyFasPredictions_GPT' && 
                                        'Generates a 1-hour price forecast for FAS using gpt only. Uses short-term timeframe weighting (5m, 15m, 1h, 4h).'
                                    }
                                    {selectedJob === 'HourlyFasPredictions_Gemini' && 
                                        'Generates a 1-hour price forecast for FAS using Gemini only. Uses short-term timeframe weighting (5m, 15m, 1h, 4h).'
                                    }
                                    
                                    {/* TMF Hourly Predictions */}
                                    {selectedJob === 'HourlyTmfPredictions' && 
                                        'Generates 1-hour price forecasts for TMF (treasury bull ETF) using both gpt and Claude. Uses short-term timeframe weighting.'
                                    }
                                    {selectedJob === 'HourlyTmfPredictions_GPT' && 
                                        'Generates a 1-hour price forecast for TMF using gpt only. Uses short-term timeframe weighting (5m, 15m, 1h, 4h).'
                                    }
                                    {selectedJob === 'HourlyTmfPredictions_Gemini' && 
                                        'Generates a 1-hour price forecast for TMF using Gemini only. Uses short-term timeframe weighting (5m, 15m, 1h, 4h).'
                                    }
                                    
                                    {/* LABU Hourly Predictions */}
                                    {selectedJob === 'HourlyLabuPredictions' && 
                                        'Generates 1-hour price forecasts for LABU (biotech bull ETF) using both gpt and Claude. Uses short-term timeframe weighting.'
                                    }
                                    {selectedJob === 'HourlyLabuPredictions_GPT' && 
                                        'Generates a 1-hour price forecast for LABU using gpt only. Uses short-term timeframe weighting (5m, 15m, 1h, 4h).'
                                    }
                                    {selectedJob === 'HourlyLabuPredictions_Gemini' && 
                                        'Generates a 1-hour price forecast for LABU using Gemini only. Uses short-term timeframe weighting (5m, 15m, 1h, 4h).'
                                    }
                                    
                                    {/* UCO Hourly Predictions */}
                                    {selectedJob === 'HourlyUcoPredictions' && 
                                        'Generates 1-hour price forecasts for UCO (oil bull ETF) using both gpt and Claude. Uses short-term timeframe weighting.'
                                    }
                                    {selectedJob === 'HourlyUcoPredictions_GPT' && 
                                        'Generates a 1-hour price forecast for UCO using gpt only. Uses short-term timeframe weighting (5m, 15m, 1h, 4h).'
                                    }
                                    {selectedJob === 'HourlyUcoPredictions_Gemini' && 
                                        'Generates a 1-hour price forecast for UCO using Gemini only. Uses short-term timeframe weighting (5m, 15m, 1h, 4h).'
                                    }
                                    
                                    {/* HF Hourly Predictions */}
                                    {selectedJob === 'HourlyHFPrediction' && 
                                        'Generates 1-hour price forecasts for HF using both gpt and Claude. Uses short-term timeframe weighting (5m, 15m, 1h, 4h).'
                                    }
                                    {selectedJob === 'HourlyHFPrediction_GPT' && 
                                        'Generates a 1-hour price forecast for HF using gpt only. Uses short-term timeframe weighting (5m, 15m, 1h, 4h).'
                                    }
                                    {selectedJob === 'HourlyHFPrediction_Gemini' && 
                                        'Generates a 1-hour price forecast for HF using Gemini only. Uses short-term timeframe weighting (5m, 15m, 1h, 4h).'
                                    }
                                </small>
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={closeTriggerModal}>
                            Cancel
                        </Button>
                        <Button 
                            color="primary" 
                            onClick={() => triggerJob(selectedJob)}
                            disabled={
                                !selectedJob || 
                                triggerLoading ||
                                (selectedJob === 'SpecificPrediction' && (!selectedModel || !selectedTimeframe))
                            }
                        >
                            {triggerLoading ? (
                                <Spinner size="sm" />
                            ) : (
                                selectedJob === 'SpecificPrediction' 
                                    ? `Trigger ${selectedModel} ${selectedTimeframe} Prediction for ${selectedSymbol}`
                                    : `Trigger ${isPredictionJob(selectedJob) ? `${selectedSymbol} ` : ''}Job`
                            )}
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* Manual Scoring Card */}
                <Card className="mt-3">
                    <CardHeader>
                        <h5 className="mb-0"><i className="ri-scales-3-line me-2"></i>Manual Scoring</h5>
                    </CardHeader>
                    <CardBody>
                        <Row className="align-items-end g-3">
                            <Col md={6} lg={4}>
                                <Label className="form-label">Rescore predictions expired since</Label>
                                <Input type="datetime-local" value={rescoreSince} onChange={(e) => setRescoreSince(e.target.value)} />
                                <small className="text-muted">Predictions with valid-until after this time (and before now) will be rescored.</small>
                            </Col>
                            <Col md="auto">
                                <Button color="warning" onClick={triggerManualRescore} disabled={rescoreLoading || !rescoreSince}>
                                    {rescoreLoading ? (<><Spinner size="sm" className="me-2" />Rescoring…</>) : (<><i className="ri-refresh-line me-1"></i> Rescore Since</>)}
                                </Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default JobManagementDashboard;
